// components/common/PricingDisplay.tsx
import React from 'react';
import {
    Box,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { usePricing, PricingBreakdown } from '../../hooks/quoter/usePricing';
import type { QuoteData } from '../../types/quoter/types';
import { useAuth } from '../../hooks/auth/useAuth';

interface PricingDisplayProps {
    quoteData: QuoteData | null;
    variant?: 'full' | 'compact' | 'print' | 'simple';
    showDisclaimer?: boolean;
    /** When true, render as a customer (no reseller section, customer labels). */
    viewAsCustomer?: boolean;
}

export const PricingDisplay: React.FC<PricingDisplayProps> = ({
    quoteData,
    variant = 'full',
    showDisclaimer = true,
    viewAsCustomer = false
}) => {
    const theme = useTheme();
    const { user } = useAuth();
    const isResellerUser = !!(user?.isAdmin || user?.isReseller);
    // Use caller's intent: when viewAsCustomer is true, suppress reseller view.
    const isResellerView = viewAsCustomer ? false : isResellerUser;
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const pricing = usePricing(quoteData);

    if (!pricing) {
        return (
            <Typography variant="body1" color="text.secondary">
                Pricing information not available
            </Typography>
        );
    }

    const isCompact = variant === 'compact';
    const isPrint = variant === 'print';
    const isSimple = variant === 'simple';

    // Simple variant - show discount breakdown if applicable
    if (isSimple) {
        const hasDiscount = pricing.retailPrice !== pricing.customerPrice && pricing.customerDiscountAmount !== 0;

        if (hasDiscount) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            component="span"
                            sx={{ fontSize: '0.875rem', textDecoration: 'line-through', color: 'text.secondary' }}
                        >
                            ${Math.round(pricing.retailPrice).toLocaleString()}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: '0.875rem', color: 'success.main', fontWeight: 'bold' }}
                        >
                            -{pricing.customerDiscountPercent.toFixed(0)}%
                        </Typography>
                    </Box>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            color: 'primary.main'
                        }}
                    >
                        {pricing.customerPrice ? `$${Math.round(pricing.customerPrice).toLocaleString()}` : 'Contact Us For Price'}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                    component="span"
                    fontWeight="bold"
                    sx={{ fontSize: '0.875rem' }}
                >
                    Price:
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        color: 'primary.main'
                    }}
                >
                    {pricing.customerPrice ? `$${Math.round(pricing.customerPrice).toLocaleString()}` : 'Contact Us For Price'}
                </Typography>
            </Box>
        );
    }

    // Grid configuration based on variant
    const gridConfig = {
        display: 'grid',
        gridTemplateColumns: isCompact || isMobile
            ? '1fr'
            : 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: isCompact ? 1 : 2,
        textAlign: 'center' as const,
        mb: isCompact ? 1 : 2
    };

    const renderCustomerSection = () => (
        <Box sx={gridConfig}>
            {/* Retail Price */}
            {pricing.showRetailPrice && (
                <Box>
                    <Typography
                        component="div"
                        fontWeight="bold"
                        sx={{ fontSize: isCompact ? '0.8rem' : '0.875rem', mb: 0.5 }}
                    >
                        Retail Price:
                    </Typography>
                    <Typography
                        component="div"
                        sx={{ fontSize: isCompact ? '0.9rem' : '1rem' }}
                    >
                        ${Math.round(pricing.retailPrice).toLocaleString()}
                    </Typography>
                </Box>
            )}

            {/* Customer Discount Percentage */}
            {pricing.showCustomerDiscount && (
                <Box>
                    <Typography
                        component="div"
                        fontWeight="bold"
                        sx={{ fontSize: isCompact ? '0.8rem' : '0.875rem', mb: 0.5 }}
                    >
                        {isResellerView ? 'Cust. Discount:' : 'Discount:'}
                    </Typography>
                    <Typography
                        component="div"
                        sx={{ fontSize: isCompact ? '0.9rem' : '1rem' }}
                    >
                        {pricing.customerDiscountPercent.toFixed(0)}%
                    </Typography>
                </Box>
            )}

            {/* Customer Discount Dollar Amount */}
            {pricing.showCustomerDiscount && pricing.customerDiscountAmount !== 0 && (
                <Box>
                    <Typography
                        component="div"
                        fontWeight="bold"
                        sx={{ fontSize: isCompact ? '0.8rem' : '0.875rem', mb: 0.5 }}
                    >
                        {isResellerView ? 'Cust. Discount:' : 'Discount:'}
                    </Typography>
                    <Typography
                        component="div"
                        sx={{ fontSize: isCompact ? '0.9rem' : '1rem' }}
                    >
                        ${Math.round(pricing.customerDiscountAmount).toLocaleString()}
                    </Typography>
                </Box>
            )}

            {/* Customer Price */}
            {pricing.showRetailPrice && (
                <Box>
                    <Typography
                        component="div"
                        fontWeight="bold"
                        sx={{ fontSize: isCompact ? '0.8rem' : '0.875rem', mb: 0.5 }}
                    >
                        {isResellerView ? '*Cust. Price:' : '*Price:'}
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            fontSize: isCompact ? '0.9rem' : '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {pricing.customerPrice ? `$${Math.round(pricing.customerPrice).toLocaleString()}` : 'Contact Us For Price'}
                    </Typography>
                </Box>
            )}
        </Box>
    );

    const renderResellerSection = () => (
        <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
            {/* Your Discount: percentage and dollar amount on same line */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mr: '60px' }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Typography
                        component="div"
                        fontWeight="bold"
                        sx={{ fontSize: isCompact ? '0.8rem' : '0.875rem' }}
                    >
                        Your Discount:
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            fontSize: isCompact ? '0.9rem' : '1rem',
                            color: isPrint ? 'inherit' : 'primary.main'
                        }}
                    >
                        ({pricing.resellerDiscountPercent.toFixed(0)}%)
                    </Typography>
                </Box>
                {pricing.resellerDiscountAmount !== 0 && (
                    <Typography
                        component="div"
                        sx={{
                            fontSize: isCompact ? '0.9rem' : '1rem',
                            color: isPrint ? 'inherit' : 'primary.main',
                            ml: 1.5 
                        }}
                    >
                        ${Math.round(pricing.resellerDiscountAmount).toLocaleString()}
                    </Typography>
                )}
            </Box>

            {/* Your Price on its own line */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mr: '60px' }}>
                <Typography
                    component="div"
                    fontWeight="bold"
                    sx={{ fontSize: isCompact ? '0.8rem' : '0.875rem' }}
                >
                    *Your Price:
                </Typography>
                <Typography
                    component="div"
                    sx={{
                        fontSize: isCompact ? '0.9rem' : '1rem',
                        fontWeight: 'bold',
                        color: isPrint ? 'inherit' : 'primary.main'
                    }}
                >
                    ${Math.round(pricing.resellerPrice).toLocaleString()}
                </Typography>
            </Box>
        </Box>
    );

    const renderSimplePrice = () => (
        <Box sx={{ textAlign: 'center' }}>
            <Typography
                component="div"
                fontWeight="bold"
                sx={{ fontSize: isCompact ? '0.8rem' : '0.875rem', mb: 0.5 }}
            >
                *Price:
            </Typography>
            <Typography
                component="div"
                sx={{
                    fontSize: isCompact ? '1rem' : '1.25rem',
                    fontWeight: 'bold',
                    color: isPrint ? 'inherit' : 'primary.main'
                }}
            >
                {pricing.finalPrice ? `$${Math.round(pricing.finalPrice).toLocaleString()}` : 'Contact Us For Price'}
            </Typography>
        </Box>
    );

    return (
        <Box>
            {/* Customer Section */}
            {pricing.hasDiscounts ? renderCustomerSection() : renderSimplePrice()}

            {/* Reseller Section - hidden for now, logic preserved */}
            {/* {isResellerView && renderResellerSection()} */}

            {/* Disclaimer */}
            {showDisclaimer && (
                <Box sx={{
                    mt: 1,
                    pt: 1,
                    borderTop: 1,
                    borderColor: 'divider'
                }}>
                    <Typography
                        variant={isCompact ? "caption" : "body2"}
                        align="center"
                        color="text.secondary"
                    >
                        *All prices displayed are estimates and subject to review. Cloth and option price adjustments are approximate as some options may impact the sail area.
                        On occasion, there are options that are not known until the sail design is available, in which case we will contact you regarding any additional costs.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default PricingDisplay;